import type { CustomizationDraft, PiclickWP } from '@/types/piclick';
import { formatBRL } from '@/lib/price';
import { LETTER_SYMBOL_ID } from '@/lib/customization';

type Props = {
  draft: CustomizationDraft;
  options: PiclickWP;
  total: number;
};

export function ReviewStep({ draft, options, total }: Props) {
  const size = options.sizes.find((s) => s.id === draft.sizeId);
  const baseColor = options.baseColors.find((c) => c.id === draft.baseColorId);
  const accessory = options.accessories.find((a) => a.id === draft.accessoryId);
  const accessoryFee = accessory?.price ?? 0;

  return (
    <div className="space-y-4">
      <ul className="divide-y-2 divide-plum/10 rounded-[1.25rem] border-2 border-plum/15 bg-white">
        <Row label="Tamanho" value={size?.label ?? '—'} />
        <Row
          label="Cor da base"
          value={baseColor?.label ?? '—'}
          colorHex={baseColor?.hex}
        />
        {draft.keys.map((k, i) => {
          const color = options.keyColors.find((c) => c.id === k.colorId);
          const symbol = options.symbols.find((s) => s.id === k.symbolId);
          const value =
            symbol?.id === LETTER_SYMBOL_ID && k.letterValue
              ? `Letra ${k.letterValue} (${color?.label ?? '—'})`
              : symbol
                ? `${symbol.label} (${color?.label ?? '—'})`
                : '—';
          return (
            <Row
              key={i}
              label={`Tecla ${i + 1}`}
              value={value}
              colorHex={color?.hex}
            />
          );
        })}
        <Row
          label="Acessório"
          value={
            accessory
              ? accessoryFee > 0
                ? `${accessory.label} (+${formatBRL(accessoryFee)})`
                : accessory.label
              : '—'
          }
        />
      </ul>

      <div className="rounded-[1.25rem] border-2 border-plum/15 bg-cream/60 p-4">
        <div className="flex items-end justify-between gap-3">
          <span className="font-action text-xs font-bold uppercase tracking-wide text-plum/70">
            Total
          </span>
          <span className="font-action text-3xl font-bold tabular-nums leading-none text-plum">
            {formatBRL(total)}
          </span>
        </div>
        {size && (
          <p className="mt-1 text-right text-xs text-plum/60">
            Base {formatBRL(size.price)}
            {accessoryFee > 0 && ` + Acessório ${formatBRL(accessoryFee)}`}
          </p>
        )}
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  colorHex,
}: {
  label: string;
  value: string;
  colorHex?: string;
}) {
  return (
    <li className="flex items-center justify-between gap-3 px-4 py-3">
      <span className="font-action text-xs font-bold uppercase tracking-wide text-plum/70 sm:text-sm">
        {label}
      </span>
      <span className="flex items-center gap-2 text-right font-action text-sm font-bold text-plum sm:text-base">
        {colorHex && (
          <span
            aria-hidden="true"
            className="block h-4 w-4 rounded-full border-2 border-plum/30"
            style={{ backgroundColor: colorHex }}
          />
        )}
        {value}
      </span>
    </li>
  );
}
